import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Crown, Sparkles } from "lucide-react";

export default function ProSuccess() {
  const location = useLocation();
  const nav = useNavigate();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState("polling"); // polling | paid | timeout | error

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sid = params.get("session_id");
    if (!sid) { setStatus("error"); return; }
    let tries = 0;
    let cancelled = false;
    const iv = setInterval(async () => {
      tries += 1;
      try {
        const r = await api.get(`/subscription/status/${sid}`);
        if (r.data?.payment_status === "paid") {
          clearInterval(iv);
          if (!cancelled) {
            setStatus("paid");
            checkAuth?.();
            setTimeout(() => nav("/pro", { replace: true }), 1600);
          }
        }
      } catch { /* keep polling */ }
      if (tries >= 20) {
        clearInterval(iv);
        if (!cancelled) setStatus("timeout");
      }
    }, 1500);
    return () => { cancelled = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 relative overflow-hidden">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(circle at 50% 40%, rgba(176,38,255,0.3), transparent 60%)" }}
      />
      <div className="relative text-center max-w-md" data-testid="pro-success-card">
        <div className="inline-flex h-16 w-16 items-center justify-center border border-[#B026FF]/50 bg-[#B026FF]/10 mb-6">
          {status === "paid"
            ? <Crown className="h-8 w-8 text-[#B026FF]" strokeWidth={2.2} />
            : status === "polling"
              ? <Loader2 className="h-8 w-8 text-[#B026FF] animate-spin" />
              : <Sparkles className="h-8 w-8 text-white/60" />}
        </div>
        {status === "polling" && (
          <>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#B026FF] mb-2">// confirming.upgrade</p>
            <h1 className="font-display font-black text-3xl text-white">syncing your pro pass…</h1>
            <p className="text-white/40 text-sm mt-2 font-mono">stripe.receipt.propagating</p>
          </>
        )}
        {status === "paid" && (
          <>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#39FF14] mb-2">// upgrade.confirmed</p>
            <h1 className="font-display font-black text-4xl text-white glitch" data-text="WELCOME TO PRO.">WELCOME TO PRO.</h1>
            <p className="text-white/50 text-sm mt-3 font-mono">redirecting…</p>
          </>
        )}
        {status === "timeout" && (
          <>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFB800] mb-2">// still.confirming</p>
            <h1 className="font-display font-black text-2xl text-white">Hang tight — Stripe is still catching up.</h1>
            <p className="text-white/40 text-sm mt-2">Refresh this page in a few seconds. Your payment is safe.</p>
          </>
        )}
        {status === "error" && (
          <>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF007F] mb-2">// missing.session</p>
            <h1 className="font-display font-black text-2xl text-white">Something didn't line up.</h1>
            <button
              onClick={() => nav("/pro", { replace: true })}
              className="mt-4 px-5 py-2.5 border border-white/20 hover:border-white/50 font-bold uppercase tracking-widest text-xs"
              data-testid="pro-success-back"
            >
              back to pro
            </button>
          </>
        )}
      </div>
    </div>
  );
}
